import { useState, useEffect, useCallback } from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  useIonRouter
} from '@ionic/react';
import { useParams } from 'react-router';
import { 
  chatbubblesOutline, 
  pieChartOutline,
  sadOutline,
  happyOutline,
  personCircleOutline,
  downloadOutline
} from 'ionicons/icons';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import Loader from '../components/Loader';
import API_CONFIG from '../config/api';

const AdminPollSentiments = () => {
  const { id } = useParams();
  const router = useIonRouter();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportSentimentReport = async () => {
    setIsExporting(true);

    // --- HACK TO PREVENT HTML2CANVAS STENCIL CRASH ---
    let originalAdoptedSet;
    const descriptor = Object.getOwnPropertyDescriptor(ShadowRoot.prototype, 'adoptedStyleSheets');
    if (descriptor && descriptor.set) {
        originalAdoptedSet = descriptor.set;
        Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
            set: function(sheets) {
                try {
                    originalAdoptedSet.call(this, sheets);
                } catch (e) {
                    // Suppress "Sharing constructed stylesheets" crash
                }
            },
            configurable: true
        });
    }

    setTimeout(async () => {
      try {
        const element = document.getElementById('sentiment-export-container');
        if (!element) return;
        
        if (typeof window.html2pdf === 'undefined') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }
        
        const opt = {
          margin:       0.3,
          filename:     `poll-${id}-sentiments.pdf`,
          image:        { type: 'jpeg', quality: 0.98 },
          html2canvas:  { scale: 2, useCORS: true, logging: false },
          jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' }
        };
        
        await window.html2pdf().set(opt).from(element).save();
      } catch (err) {
        console.error('Failed to export PDF:', err);
      } finally {
        setIsExporting(false);
        // Restore original setter
        if (originalAdoptedSet) {
            Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
                set: originalAdoptedSet,
                configurable: true
            });
        }
      }
    }, 800);
  };

  const fetchSentiments = useCallback(async () => {
    setLoading(true);
    let safetyTimeout = setTimeout(() => setLoading(false), 12000);
    try {
      const response = await axios.get(`${API_CONFIG.GET_POLL_SENTIMENTS}?poll_id=${id}`, { timeout: 8000 });
      if (response.data.success) {
        setData(response.data);
      } else {
        setError(response.data.error || 'Failed to load sentiments.');
      }
    } catch (err) {
      console.error('Error fetching sentiments:', err);
      setError('Could not connect to server.');
    } finally {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSentiments();
  }, [fetchSentiments]);

  // Transform data for recharts
  const processChartData = () => {
    if (!data || !data.sentiments) return [];
    
    // Group sentiments by answer_text
    const grouped = data.sentiments.reduce((acc, curr) => {
      const option = curr.answer_text;
      if (!acc[option]) {
        acc[option] = 0;
      }
      acc[option]++;
      return acc;
    }, {});

    // Format for Recharts
    return Object.keys(grouped).map(key => ({
      name: (key.length > 20 ? key.substring(0, 20) + '...' : key), 
      fullName: key,
      feedbacks: grouped[key]
    })).sort((a, b) => b.feedbacks - a.feedbacks);
  };

  const chartData = processChartData();
  const colors = ['#6366f1', '#14b8a6', '#f59e0b', '#ec4899', '#8b5cf6'];

  return (
    <IonPage>
      <IonHeader className="ion-no-border" translucent={true}>
        <IonToolbar className="header-toolbar frosted-header">
          <IonButtons slot="start">
            <IonBackButton text="Back" defaultHref="/admin/polls" />
          </IonButtons>
          <IonTitle>Sentiment Analytics</IonTitle>
          <IonButtons slot="end">
            <IonButton 
              className="export-btn-premium mr-2"
              onClick={exportSentimentReport} 
              disabled={loading || isExporting || !data}
              style={{ '--color': '#6366f1', fontWeight: 'bold' }}
            >
              <IonIcon icon={downloadOutline} slot="start" />
              EXPORT REPORT
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-slate-50">
        {loading && <Loader message="Analyzing Responses..." />}
        
        {error ? (
          <div className="p-6 text-center text-red-600 bg-red-50 m-4 rounded-xl border border-red-200">
            <IonIcon icon={sadOutline} className="text-4xl mb-2" />
            <p className="font-semibold">{error}</p>
            <IonButton className="mt-4" onClick={fetchSentiments}>Retry</IonButton>
          </div>
        ) : data && (
          <div id="sentiment-export-container" className={`max-w-4xl mx-auto p-4 md:p-6 space-y-6 ${isExporting ? 'bg-slate-50' : ''}`}>
            
            {isExporting && (
                <div className="mb-6 border-b pb-4">
                    <h2 className="text-2xl font-black text-slate-800">Poll Sentiment Report</h2>
                    <p className="text-sm text-slate-400 mt-2">Generated on: {new Date().toLocaleString()}</p>
                </div>
            )}

            {/* Context Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 md:p-8">
              <div className="flex items-center gap-3 text-indigo-600 mb-4">
                {isExporting ? <span className="text-2xl">💬</span> : <IonIcon icon={chatbubblesOutline} className="text-2xl" />}
                <h2 className="text-sm font-bold uppercase tracking-widest">Poll Context</h2>
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-800 leading-tight">
                {data.poll_question}
              </h1>
              <div className="mt-4 inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-sm font-bold">
                Total Feedback Received: {data.sentiments.length}
              </div>
            </div>

            {/* Chart Widget */}
            {data.sentiments.length > 0 ? (
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 md:p-8">
                <div className="flex items-center gap-3 text-teal-600 mb-6">
                  {isExporting ? <span className="text-2xl">📊</span> : <IonIcon icon={pieChartOutline} className="text-2xl" />}
                  <h2 className="text-sm font-bold uppercase tracking-widest">Feedback Distribution</h2>
                </div>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <Tooltip 
                        cursor={{ fill: '#f8fafc' }}
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        formatter={(value, name, props) => [value + ' responses', 'Feedback Count']}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                      />
                      <Bar dataKey="feedbacks" radius={[6, 6, 0, 0]} isAnimationActive={!isExporting}>
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="text-center p-12 bg-white rounded-3xl border border-slate-100 shadow-sm">
                 {isExporting ? <span className="text-6xl text-slate-300 mb-4 inline-block">😌</span> : <IonIcon icon={happyOutline} className="text-6xl text-slate-300 mb-4" />}
                 <h3 className="text-xl font-bold text-slate-600">No Sentiments Yet</h3>
                 <p className="text-slate-500 mt-2">Voters haven't provided any reasons for this poll yet.</p>
              </div>
            )}

            {/* Responses List */}
            {data.sentiments.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-800 ml-2">Qualitative Responses:</h3>
                <div className="grid gap-4">
                  {data.sentiments.map(sentiment => (
                    <div key={sentiment.id} className={`bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex items-start gap-4 transition-shadow ${isExporting ? 'page-break-inside-avoid' : 'hover:shadow-md'}`}>
                       <div className="bg-slate-100 text-slate-400 p-2 rounded-full flex items-center justify-center">
                         {isExporting ? <span className="text-xl leading-none">👤</span> : <IonIcon icon={personCircleOutline} className="text-2xl" />}
                       </div>
                       <div className="flex-1">
                         <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                           <span className="font-bold text-slate-800">
                             {sentiment.user_name || 'Anonymous Voter'}
                           </span>
                           <span className="text-xs font-semibold text-slate-500">
                             {new Date(sentiment.created_at).toLocaleString()}
                           </span>
                         </div>
                         <div className="mb-3 text-xs font-bold uppercase tracking-wide text-indigo-600 bg-indigo-50 inline-block px-2 py-1 rounded">
                           Option: {sentiment.answer_text}
                         </div>
                         <p className="text-slate-700 leading-relaxed italic">
                           "{sentiment.reason}"
                         </p>
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AdminPollSentiments;
